import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { InterventionGroupDto, TreatmentPlanDto } from '../../api/generated';
import { flattenGroups } from '../../utils/ganttUtils';
import {
  computeAffectedGroupIds,
  DEFAULT_AFFECTED_GROUPS_CHOICE,
  type AffectedGroupsChoice
} from './affectedGroups';
import styles from './AffectedGroupsPicker.module.css';

interface AffectedGroupsPickerProps {
  plan: TreatmentPlanDto;
  selectedGroup: InterventionGroupDto;
  onChange: (groupIds: string[]) => void;
}

export const AffectedGroupsPicker = ({
  plan,
  selectedGroup,
  onChange
}: AffectedGroupsPickerProps) => {
  const { t } = useTranslation();
  const groupName = useId();
  const [choice, setChoice] = useState<AffectedGroupsChoice>(DEFAULT_AFFECTED_GROUPS_CHOICE);
  const flatSelected = flattenGroups(plan).find((g) => g.id === selectedGroup.id);
  const weekLabel = flatSelected ? `${t('week-label')} ${String(flatSelected.weekNumber)}` : '';

  const handleChange = (next: AffectedGroupsChoice) => {
    setChoice(next);
    onChange(computeAffectedGroupIds(plan, selectedGroup, next));
  };

  const options: { value: AffectedGroupsChoice; label: string }[] = [
    { value: 'plan', label: t('affected-groups-all') },
    {
      value: 'onwards',
      label: `${t('affected-groups-onwards')} (${t('affected-groups-from')} ${weekLabel})`
    },
    { value: 'group', label: `${t('affected-groups-this')} (${weekLabel})` }
  ];

  return (
    <div className={styles.picker}>
      <span className={styles.label}>{t('affected-groups-apply-to')}</span>
      <div className={styles.options}>
        {options.map((opt) => (
          <label key={opt.value} className={styles.option}>
            <input
              type="radio"
              name={groupName}
              value={opt.value}
              checked={choice === opt.value}
              onChange={() => handleChange(opt.value)}
              className={styles.radio}
            />
            <span className={styles.optionLabel}>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};
